class CustomQuickView {
  constructor() {
    this.modal = document.getElementById('CustomQuickView');
    if (!this.modal) return;

    this.container = this.modal.querySelector('.custom-quick-view__container');
    this.overlay = this.modal.querySelector('.custom-quick-view__overlay');
    this.closeBtn = this.modal.querySelector('.custom-quick-view__close');
    this.content = this.modal.querySelector('.custom-quick-view__content');

    // Elements to populate
    this.image = this.modal.querySelector('.custom-quick-view__image');
    this.title = this.modal.querySelector('.custom-quick-view__title');
    this.vendor = this.modal.querySelector('.custom-quick-view__vendor');
    this.price = this.modal.querySelector('.custom-quick-view__price');
    this.optionsContainer = this.modal.querySelector('.custom-quick-view__options');
    this.atcBtn = this.modal.querySelector('.custom-quick-view__add-to-cart');
    this.buyNowBtn = this.modal.querySelector('.custom-quick-view__buy-now');
    this.qtyInput = this.modal.querySelector('#QuickViewQuantity');
    this.dotsContainer = this.modal.querySelector('.custom-quick-view__dots');

    this.product = null;
    this.selectedVariant = null;
    this.images = [];
    this.currentImageIndex = 0;
    this.carouselInterval = null;

    this.init();
  }

  init() {
    // Open triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view-trigger]');
      if (trigger) {
        e.preventDefault();
        const handle = trigger.getAttribute('data-product-handle');
        const initialImage = trigger.getAttribute('data-product-image');
        const initialTitle = trigger.getAttribute('data-product-title');
        const initialPrice = trigger.getAttribute('data-product-price');
        this.open(handle, initialImage, initialTitle, initialPrice);
      }
    });

    // Close listeners
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    // Quantity buttons
    const qtyButtons = this.modal.querySelectorAll('.quantity__button');
    qtyButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        let val = parseInt(this.qtyInput.value);
        if (btn.name === 'plus') {
          this.qtyInput.value = val + 1;
        } else if (val > 1) {
          this.qtyInput.value = val - 1;
        }
      });
    });

    // Add to cart
    this.atcBtn.addEventListener('click', () => this.addToCart());
    this.buyNowBtn.addEventListener('click', () => this.addToCart(true));
  }

  async open(handle, initialImage = null, initialTitle = null, initialPrice = null) {
    if (!handle) return;
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('is-loading'); // Show skeletons
    document.body.style.overflow = 'hidden';

    // Set initial data instantly if available
    if (initialImage) {
      this.image.src = initialImage;
      this.image.style.opacity = '1';
    }
    if (initialTitle) {
      this.title.textContent = initialTitle;
      this.title.classList.remove('loading-placeholder');
    } else {
      this.title.classList.add('loading-placeholder');
    }
    if (initialPrice) {
      this.price.textContent = initialPrice;
      this.price.classList.remove('loading-placeholder');
    } else {
      this.price.classList.add('loading-placeholder');
    }

    if (this.vendor) this.vendor.classList.add('loading-placeholder');

    // Reset state and show immediate transition
    this.content.style.opacity = '1';
    this.content.style.transform = 'translateY(0)';

    try {
      const response = await fetch(`/products/${handle}.js`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      this.product = await response.json();
      this.modal.classList.remove('is-loading');
      this.title.classList.remove('loading-placeholder');
      this.price.classList.remove('loading-placeholder');
      this.vendor.classList.remove('loading-placeholder');

      this.render();
    } catch (error) {
      this.modal.classList.remove('is-loading');
      this.title.classList.remove('loading-placeholder');
      this.price.classList.remove('loading-placeholder');
      this.vendor.classList.remove('loading-placeholder');
      console.error('Failed to fetch product:', error);
      alert('Sorry, could not load product details.');
      this.close();
    }
  }

  close() {
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    this.stopCarousel();

    // Reverse content animation
    this.content.style.transition = 'all 0.3s ease';
    this.content.style.opacity = '0';
    this.content.style.transform = 'translateY(10px)';

    setTimeout(() => {
      this.reset();
    }, 500);
  }

  reset() {
    this.image.style.opacity = '0';
    this.image.src = '';
    this.title.textContent = '';
    if (this.vendor) this.vendor.textContent = '';
    this.price.textContent = '';
    this.optionsContainer.innerHTML = '';
    this.qtyInput.value = '1';
    this.selectedVariant = null;
    this.product = null;

    if (this.dotsContainer) this.dotsContainer.innerHTML = '';
    this.images = [];
    this.currentImageIndex = 0;
    this.stopCarousel();
  }

  render() {
    const p = this.product;
    this.title.textContent = p.title;
    if (this.vendor) this.vendor.textContent = p.vendor;

    this.images = p.images || [];
    if (this.images.length === 0 && p.featured_image) {
      this.images = [p.featured_image];
    }

    this.renderDots();
    this.goToImage(0);
    this.startCarousel();

    // Price formatting
    this.updatePrice(p.variants[0]);

    // Render variants
    this.renderOptions();

    // Select first available variant
    this.selectVariant(p.variants[0]);
  }

  renderDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    if (this.images.length <= 1) return;

    this.images.forEach((_, index) => {
      const dot = document.createElement('div');
      dot.className = 'custom-quick-view__dot';
      if (index === 0) dot.classList.add('active');

      dot.addEventListener('click', () => {
        this.goToImage(index);
        this.stopCarousel();
        this.startCarousel();
      });

      this.dotsContainer.appendChild(dot);
    });
  }

  goToImage(index) {
    if (this.images.length === 0) return;
    this.currentImageIndex = index;

    let imageUrl = this.images[index];
    if (typeof imageUrl !== 'string') {
      imageUrl = imageUrl.src || imageUrl;
    }

    this.image.style.opacity = '0';
    setTimeout(() => {
      this.image.src = imageUrl;
      this.image.onload = () => {
        this.image.style.transition = 'opacity 0.3s ease';
        this.image.style.opacity = '1';
      };
    }, 100);

    if (this.dotsContainer) {
      const dots = this.dotsContainer.querySelectorAll('.custom-quick-view__dot');
      dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }

  startCarousel() {
    if (this.images.length <= 1) return;
    this.stopCarousel();
    this.carouselInterval = setInterval(() => {
      let nextIndex = (this.currentImageIndex + 1) % this.images.length;
      this.goToImage(nextIndex);
    }, 3000);
  }

  stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  renderOptions() {
    this.optionsContainer.innerHTML = '';

    this.product.options.forEach((option, index) => {
      if (option.name === 'Title' && option.values.length === 1) return;

      const optionWrapper = document.createElement('div');
      optionWrapper.className = 'custom-quick-view__option';

      const label = document.createElement('span');
      label.className = 'custom-quick-view__option-label';
      label.textContent = option.name;
      optionWrapper.appendChild(label);

      const valuesWrapper = document.createElement('div');
      valuesWrapper.className = 'custom-quick-view__option-values';

      option.values.forEach(value => {
        const valBtn = document.createElement('div');
        valBtn.className = 'custom-quick-view__option-value';
        valBtn.textContent = value;
        valBtn.dataset.optionIndex = index;
        valBtn.dataset.value = value;

        valBtn.addEventListener('click', () => {
          this.handleOptionClick(index, value, valBtn);
        });

        valuesWrapper.appendChild(valBtn);
      });

      optionWrapper.appendChild(valuesWrapper);
      this.optionsContainer.appendChild(optionWrapper);
    });
  }

  handleOptionClick(index, value, element) {
    // Update UI
    const siblings = element.parentNode.querySelectorAll('.custom-quick-view__option-value');
    siblings.forEach(s => s.classList.remove('selected'));
    element.classList.add('selected');

    // Find selected variant
    this.updateSelectedVariant();
  }

  updateSelectedVariant() {
    const selectedOptions = Array.from(this.modal.querySelectorAll('.custom-quick-view__option-value.selected'))
      .map(el => el.dataset.value);

    const variant = this.product.variants.find(v => {
      return v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    if (variant) {
      this.selectVariant(variant);
    }
  }

  selectVariant(variant) {
    this.selectedVariant = variant;
    this.updatePrice(variant);

    if (variant.featured_image) {
      let src = variant.featured_image.src;
      // Extract filename without query params for matching
      let filename = src.split('/').pop().split('?')[0];

      let foundIndex = this.images.findIndex(img => {
        let imgSrc = typeof img === 'string' ? img : (img.src || '');
        return imgSrc.includes(filename);
      });

      if (foundIndex !== -1) {
        this.goToImage(foundIndex);
        this.stopCarousel();
        this.startCarousel();
      } else {
        this.image.src = src;
      }
    }

    if (variant.available) {
      this.atcBtn.disabled = false;
      this.atcBtn.querySelector('span').textContent = 'ADD TO CART';
    } else {
      this.atcBtn.disabled = true;
      this.atcBtn.querySelector('span').textContent = 'SOLD OUT';
    }

    // Update selected class in UI
    variant.options.forEach((opt, i) => {
      const btn = this.modal.querySelector(`.custom-quick-view__option-value[data-option-index="${i}"][data-value="${opt.replace(/"/g, '\\"')}"]`);
      if (btn) btn.classList.add('selected');
    });
  }

  updatePrice(variant) {
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    });
    this.price.textContent = formatter.format(variant.price / 100);
  }

  async addToCart(goToCheckout = false) {
    if (!this.selectedVariant) return;

    this.atcBtn.classList.add('loading');
    this.atcBtn.setAttribute('disabled', 'true');
    this.atcBtn.querySelector('span').textContent = 'ADDING...';
    this.modal.querySelector('.loading-overlay__spinner').classList.remove('hidden');

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const formData = new FormData();
    formData.append('id', this.selectedVariant.id);
    formData.append('quantity', parseInt(this.qtyInput.value));

    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      });

      const responseData = await response.json();

      if (goToCheckout) {
        window.location.href = window.routes.cart_url;
      } else {
        this.close();
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(responseData);
        } else {
          window.location.href = window.routes.cart_url;
        }
      }
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.atcBtn.querySelector('span').textContent = 'ERROR - TRY AGAIN';
    } finally {
      this.atcBtn.classList.remove('loading');
      this.atcBtn.removeAttribute('disabled');
      if (!this.atcBtn.disabled) {
        this.atcBtn.querySelector('span').textContent = 'ADD TO CART';
      }
      this.modal.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.customQuickView = new CustomQuickView();
});
